import { Injectable, inject, signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { MsalService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  IdVerificationStatusUpdate,
  ParticipantStatusUpdate,
  SigningStatusUpdate,
} from './esign-hub.model';

@Injectable({
  providedIn: 'root',
})
export class ESignHubService {
  private readonly msalService = inject(MsalService);
  private connection: HubConnection | null = null;

  readonly connected = signal(false);
  readonly signingStatusUpdate$ = new Subject<SigningStatusUpdate>();
  readonly participantStatusUpdate$ = new Subject<ParticipantStatusUpdate>();
  readonly idVerificationStatusUpdate$ = new Subject<IdVerificationStatusUpdate>();
  readonly allPresignComplete$ = new Subject<void>();

  async connectAsync(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) {
      return;
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(`${environment.apiBaseUrl}/api/v1/hubs/esign`, {
        accessTokenFactory: () => this.getAccessToken(),
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on('ReceiveSigningStatus', (update: SigningStatusUpdate) => {
      this.signingStatusUpdate$.next(update);
    });

    this.connection.on('ReceiveParticipantProgress', (update: ParticipantStatusUpdate) => {
      this.participantStatusUpdate$.next(update);
    });

    this.connection.on('ReceiveIdVerificationStatus', (update: IdVerificationStatusUpdate) => {
      this.idVerificationStatusUpdate$.next(update);
    });

    this.connection.on('ReceiveAllPresignComplete', () => {
      this.allPresignComplete$.next();
    });

    this.connection.onreconnecting(() => {
      this.connected.set(false);
    });

    this.connection.onreconnected(() => {
      this.connected.set(true);
    });

    this.connection.onclose(() => {
      this.connected.set(false);
      this.retryConnection();
    });

    await this.connection.start();
    this.connected.set(true);
  }

  async disconnectAsync(): Promise<void> {
    await this.connection?.stop();
    this.connected.set(false);
    this.connection = null;
  }

  private retryConnection(): void {
    setTimeout(async () => {
      try {
        await this.connection?.start();
        this.connected.set(true);
      } catch {
        this.retryConnection();
      }
    }, 30_000);
  }

  private async getAccessToken(): Promise<string> {
    const result = await this.msalService.instance.acquireTokenSilent({
      scopes: environment.msal.consentScopes,
    });
    return result.accessToken;
  }
}
